import { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Form } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Appearance } from '@Components/designConstants';
import { Checkbox } from '@Components/FormCheck';
import { useSetError } from '../../../redux/modals';
import { ClientType, ClientTypeType, isClientType, ScanInput } from '../digital-audit-constants';
import { normalizeScanUrl } from '../digital-audit-utils';
import { CLIENT_TYPE_OPTIONS, VERTICAL_OPTIONS } from '../domain-config-constants';
import { useDomainConfigResolve } from '../use-domain-config';

// How long to wait after the user stops typing a URL before resolving its
// domain config - long enough that normal typing doesn't fire a request per
// keystroke, short enough that the lock/prefill feels responsive once they pause.
const URL_RESOLVE_DEBOUNCE_MS = 600;

interface ScanInputPanelProps {
  onScan: (input: ScanInput, alsoRunFullScan: boolean) => void;
  onReuseLastFindings?: (runId: string) => void;
  loading?: boolean;
}

export const ScanInputPanel = ({
  onScan,
  onReuseLastFindings = () => {},
  loading = false
}: ScanInputPanelProps) => {
  const setError = useSetError();
  const [url, setUrl] = useState('');
  const [debouncedUrl, setDebouncedUrl] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientType, setClientType] = useState<ClientTypeType>(ClientType.ECOMM);
  const [vertical, setVertical] = useState('other');
  const [alsoRunFullScan, setAlsoRunFullScan] = useState(false);
  const [consentSelector, setConsentSelector] = useState('');

  // Debounced resolve target - only recomputed once the user pauses typing,
  // so this doesn't fire a request on every keystroke.
  useEffect(() => {
    const trimmed = url.trim();
    if (trimmed.length === 0) {
      setDebouncedUrl('');
      return undefined;
    }
    const timeoutId = setTimeout(
      () => setDebouncedUrl(normalizeScanUrl(trimmed)),
      URL_RESOLVE_DEBOUNCE_MS
    );
    return () => clearTimeout(timeoutId);
  }, [url]);

  const domainConfigQuery = useDomainConfigResolve(debouncedUrl);
  // undefined = not resolved yet (still loading or no URL); null = resolved,
  // but this is a brand-new domain - both render as "no lock", only a
  // present result locks the fields.
  const resolved = domainConfigQuery.data;

  // A real resolve failure (not the expected 404-for-a-new-domain case,
  // which resolveDomainConfig already normalizes to `null`) still deserves
  // the shared error modal like any other React Query error.
  useEffect(() => {
    if (!domainConfigQuery.error) return;
    setError({
      title: "Couldn't check for an existing domain config",
      message: `Failed to look up this domain: ${domainConfigQuery.error.message}`,
      reportError: domainConfigQuery.error
    });
  }, [domainConfigQuery.error, setError]);

  // The resolved config wins over whatever the user had locally selected -
  // pre-fill client type/vertical from it whenever a new resolve comes in.
  useEffect(() => {
    if (!resolved) return;
    setClientType(resolved.config.client_type);
    setVertical(resolved.config.vertical);
  }, [resolved]);

  const canSubmit = url.trim().length > 0 && !loading;
  const isLocked = Boolean(resolved);

  const handleSubmit = () => {
    const normalizedUrl = normalizeScanUrl(url);
    setUrl(normalizedUrl);
    onScan(
      {
        url: normalizedUrl,
        clientName: clientName.trim(),
        clientType,
        vertical: vertical.trim(),
        consentSelector: consentSelector.trim() || undefined
      },
      alsoRunFullScan
    );
  };

  return (
    <div className='digital-audit-scan-input-panel'>
      <div className='digital-audit-section-title'>Scan a website</div>
      <div className='digital-audit-subtitle'>
        Paste a URL to detect its martech stack, align it to a customer journey, and score channel
        health.
      </div>
      <Form.Group>
        <Form.Label>Website URL</Form.Label>
        <Form.Control
          type='text'
          placeholder='example.com'
          value={url}
          onChange={e => setUrl(e.target.value)}
        />
      </Form.Group>
      <Form.Group>
        <Form.Label>Client Name</Form.Label>
        <Form.Control
          type='text'
          placeholder='Optional, cosmetic only'
          value={clientName}
          onChange={e => setClientName(e.target.value)}
        />
      </Form.Group>
      <Form.Group>
        <Form.Label>Client Type</Form.Label>
        <Form.Select
          disabled={isLocked}
          value={clientType}
          onChange={e => {
            if (isClientType(e.target.value)) {
              setClientType(e.target.value);
            }
          }}
        >
          {CLIENT_TYPE_OPTIONS.map(option => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group>
        <Form.Label>Vertical</Form.Label>
        <Form.Select
          disabled={isLocked}
          value={vertical}
          onChange={e => setVertical(e.target.value)}
        >
          {VERTICAL_OPTIONS.map(option => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      {resolved && (
        <ExistingDomainChoice
          resolved={resolved}
          onUseLastFindings={onReuseLastFindings}
          onRerun={handleSubmit}
          canSubmit={canSubmit}
          loading={loading}
        />
      )}
      <Form.Group>
        <Checkbox
          id='also-run-full-scan'
          label='Also run a full site scan'
          checked={alsoRunFullScan}
          onChange={e => setAlsoRunFullScan(e.target.checked)}
        />
        <div className='digital-audit-subtitle'>
          Crawls several key pages in a real browser in the background — watch the banner at the top
          of the report for progress, then the tabs below will update with the full results.
        </div>
      </Form.Group>
      {alsoRunFullScan && (
        <Form.Group>
          <Form.Label>Consent &quot;Accept&quot; button selector (optional)</Form.Label>
          <Form.Control
            type='text'
            placeholder='e.g. #onetrust-accept-btn-handler'
            value={consentSelector}
            onChange={e => setConsentSelector(e.target.value)}
          />
          <div className='digital-audit-subtitle'>
            A CSS selector for this site&apos;s cookie-consent accept button. If supplied, the crawl
            clicks it after the page loads and captures a second time, so tags blocked by a
            consent-denied default (see Pixel Intel) show up too.
          </div>
          <div className='digital-audit-subtitle'>
            <a
              href='https://docs.google.com/document/d/1vMt4uRcG8n2FrvyPlU7QWA-K1U5kjBY8jc4Br3eb5nc/edit?usp=sharing'
              target='_blank'
              rel='noopener noreferrer'
            >
              Need help finding this selector? See the field guide.
            </a>
          </div>
        </Form.Group>
      )}
      {!resolved?.last_run && (
        <Button disabled={!canSubmit} onClick={handleSubmit}>
          {loading ? 'Scanning…' : 'Run Scan'}
        </Button>
      )}
    </div>
  );
};

interface ExistingDomainChoiceProps {
  resolved: NonNullable<ReturnType<typeof useDomainConfigResolve>['data']>;
  onUseLastFindings: (runId: string) => void;
  onRerun: () => void;
  canSubmit: boolean;
  loading: boolean;
}

// This domain has been scanned before - let the user reuse the last saved
// report instead of re-running the whole scan, or proceed with a rerun using
// the now-locked (and pre-filled) client context.
const ExistingDomainChoice = ({
  resolved,
  onUseLastFindings,
  onRerun,
  canSubmit,
  loading
}: ExistingDomainChoiceProps) => {
  const { last_run: lastRun } = resolved;

  return (
    <div className='digital-audit-existing-domain-choice'>
      <div className='digital-audit-subtitle'>
        We&apos;ve scanned this domain before — client type and vertical are locked to match.
      </div>
      {lastRun && (
        <div className='digital-audit-existing-domain-choice-actions'>
          <Button onClick={() => onUseLastFindings(lastRun.id)}>
            Use last findings (run on {format(parseISO(lastRun.created_at), 'MMM d, yyyy')})
          </Button>
          <Button
            appearance={Appearance.OUTLINED}
            disabled={!canSubmit}
            loading={loading}
            onClick={onRerun}
          >
            Rerun and update
          </Button>
        </div>
      )}
    </div>
  );
};
